// Timeline component
// FIX: Harmonized cursor to cursor-grab/active:cursor-grabbing for consistency with team view
// CHANGE: Horizontal compact timeline with click-to-place and drag-and-drop task
// CHANGE: Shows ~8 hours at a time with horizontal scroll
// CHANGE: Navigation arrows and swipe support for timeline scrolling
// CHANGE: Dropdown selectors for hour and minutes (15min intervals)
// CHANGE: 15-minute slots visible in light gray
// CHANGE: Drag and drop with visual feedback following cursor
// CHANGE: Task titles displayed on all tasks with custom tooltips
// CHANGE: Fixed horizontal line alignment between header and timeline area
// CHANGE: Custom tooltip system with 300ms delay and complete info (title, time, duration)
// CHANGE: Fixed click positioning to place at interval start, not end
// CHANGE: Implemented magnetic snap for drag & drop on 15-min intervals
// CHANGE: Fixed vertical line alignment by using absolute positioning for both header and timeline
// CHANGE: Timeline now centers on 12:00 (noon) by default for better UX
// UPDATED: Status values changed to not_started, completed, overdue
// UPDATED: Removed integrated TimePicker (now managed by parent AssignmentModal)
// UPDATED: Removed blue summary box showing task name and time
// REFACTORED: Using centralized time calculation functions from timeCalculations
// REFACTORED: Using centralized conflict detection functions from timeCalculations
// REFACTORED: Using centralized drag & drop utilities from dragDropUtils
//   - Replaced duplicate invisible drag image creation code with createInvisibleDragImage utility

import { useRef, useState, useEffect } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { timeToPercent, durationToPercent, hasConflictWithExisting } from '../../lib/timeCalculations';
import { createInvisibleDragImage } from '../../lib/dragDropUtils';
import { formatDuration } from '../../lib/dateUtils';

interface TimelineTask {
  id: string;
  title: string;
  startTime: string;
  duration: number;
  status?: 'not_started' | 'completed' | 'overdue';
}

interface TimelineProps {
  existingTasks: TimelineTask[];
  newTask: {
    title: string;
    duration: number;
  } | null;
  newTaskTime: string;
  onNewTaskTimeChange: (time: string) => void;
}

export function Timeline({
  existingTasks,
  newTask,
  newTaskTime,
  onNewTaskTimeChange
}: TimelineProps) {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredTask, setHoveredTask] = useState<{ id: string; x: number; y: number } | null>(null);
  const tooltipTimerRef = useRef<number | null>(null);

  const generateTimeSlots = () => {
    const slots: string[] = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const h = hour.toString().padStart(2, '0');
        const m = minute.toString().padStart(2, '0');
        slots.push(`${h}:${m}`);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  const timeToSlotIndex = (time: string): number => {
    return timeSlots.indexOf(time);
  };

  // Correction #3: Modifié pour placer au DÉBUT de l'intervalle
  // Au lieu d'arrondir (Math.round), on utilise Math.floor pour toujours prendre le début
  const xPositionToTime = (x: number, width: number): string => {
    const percent = (x / width) * 100;
    const totalMinutes = (percent / 100) * 24 * 60;
    // Math.floor au lieu de Math.round pour obtenir le début de l'intervalle
    const slotMinutes = Math.floor(totalMinutes / 15) * 15;
    const hours = Math.floor(slotMinutes / 60) % 24;
    const minutes = slotMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const hasConflict = newTaskTime
    ? hasConflictWithExisting(
        { startTime: newTaskTime, duration: newTask?.duration || 30 },
        existingTasks
      )
    : false;

  const handleTimelineClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) return;
    if (!timelineRef.current) return;

    const rect = timelineRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + timelineRef.current.scrollLeft;
    const fullWidth = timelineRef.current.scrollWidth;
    const time = xPositionToTime(x, fullWidth);

    onNewTaskTimeChange(time);
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';

    // Create invisible drag image using centralized utility
    createInvisibleDragImage(e);
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    if (e.clientX !== 0 && e.clientY !== 0) {
      // Calculer et mettre à jour la position magnétique en temps réel
      if (!timelineRef.current) return;

      const rect = timelineRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left + timelineRef.current.scrollLeft;
      const fullWidth = timelineRef.current.scrollWidth;

      if (x >= 0 && x <= fullWidth) {
        const snappedTime = xPositionToTime(x, fullWidth);
        onNewTaskTimeChange(snappedTime);
      }
    }
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleScroll = () => {
    if (timelineRef.current) {
      setScrollPosition(timelineRef.current.scrollLeft);
    }
  };

  const scrollTimeline = (direction: 'left' | 'right') => {
    if (!timelineRef.current) return;

    const scrollAmount = timelineRef.current.clientWidth * 0.5;
    const newPosition = direction === 'left'
      ? Math.max(0, scrollPosition - scrollAmount)
      : Math.min(timelineRef.current.scrollWidth - timelineRef.current.clientWidth, scrollPosition + scrollAmount);

    timelineRef.current.scrollTo({
      left: newPosition,
      behavior: 'smooth'
    });
  };

  // Center timeline on 12:00 (noon) on initial render
  useEffect(() => {
    const timeline = timelineRef.current;
    if (timeline) {
      // Calculate scroll position to center on 12:00
      // 12:00 is at 50% of the timeline (12/24 hours)
      const targetPosition = (timeline.scrollWidth * 0.5) - (timeline.clientWidth * 0.5);
      timeline.scrollLeft = Math.max(0, targetPosition);
      setScrollPosition(timeline.scrollLeft);
    }
  }, []);

  useEffect(() => {
    const timeline = timelineRef.current;
    if (timeline) {
      timeline.addEventListener('scroll', handleScroll);
      return () => timeline.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleMouseEnter = (taskId: string, e: React.MouseEvent) => {
    if (isDragging) return;

    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top;

    tooltipTimerRef.current = window.setTimeout(() => {
      setHoveredTask({ id: taskId, x, y });
    }, 300);
  };

  const handleMouseLeave = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
      tooltipTimerRef.current = null;
    }
    setHoveredTask(null);
  };

  useEffect(() => {
    if (isDragging) {
      setHoveredTask(null);
      if (tooltipTimerRef.current) {
        clearTimeout(tooltipTimerRef.current);
        tooltipTimerRef.current = null;
      }
    }
  }, [isDragging]);

  const formatTimeToHHMM = (time: string): string => {
    if (!time) return '00:00';
    // Si le format contient des secondes (hh:mm:ss), on les retire
    const parts = time.split(':');
    if (parts.length >= 2) {
      return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    return time;
  };

  const getTooltipContent = (taskId: string) => {
    if (taskId === 'new-task' && newTask && newTaskTime) {
      return {
        title: newTask.title,
        time: formatTimeToHHMM(newTaskTime),
        duration: newTask.duration,
        isNew: true
      };
    }

    const task = existingTasks.find(t => t.id === taskId);
    if (task) {
      return {
        title: task.title,
        time: formatTimeToHHMM(task.startTime),
        duration: task.duration,
        isNew: false
      };
    }

    return null;
  };

  const getStatusColor = () => {
    return 'bg-gray-500';
  };

  const canScrollLeft = scrollPosition > 0;
  const canScrollRight = timelineRef.current
    ? scrollPosition < (timelineRef.current.scrollWidth - timelineRef.current.clientWidth - 10)
    : false;

  return (
    <div className="flex flex-col h-full space-y-4">
      {hasConflict && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded-lg flex items-start gap-2">
          <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold">Conflit temporel détecté</p>
            <p className="text-xs mt-1">
              Cette mission chevauche une autre mission déjà planifiée. Vous pouvez quand même l'attribuer si nécessaire.
            </p>
          </div>
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <div className="mb-4">
          <span className="text-xs font-semibold text-gray-700">
            {newTaskTime ? 'Cliquez ou glissez-déposez pour déplacer la mission' : 'Cliquez sur la timeline pour placer la mission'}
          </span>
        </div>

        <div className="relative">
          <button
            onClick={() => scrollTimeline('left')}
            disabled={!canScrollLeft}
            className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-2 shadow-lg transition-all ${
              canScrollLeft ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-50 cursor-not-allowed'
            }`}
            aria-label="Défiler vers la gauche"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>

          <button
            onClick={() => scrollTimeline('right')}
            disabled={!canScrollRight}
            className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white border border-gray-300 rounded-full p-2 shadow-lg transition-all ${
              canScrollRight ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-50 cursor-not-allowed'
            }`}
            aria-label="Défiler vers la droite"
          >
            <ChevronRight size={20} className="text-gray-600" />
          </button>

          <div
            ref={timelineRef}
            onClick={handleTimelineClick}
            onDragOver={handleDragOver}
            className="relative overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100"
            style={{
              cursor: 'pointer',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <div className="relative h-32 bg-white rounded border border-gray-300" style={{ minWidth: '300%' }}>
              {/* Header avec labels des heures et traits verticaux alignés */}
              <div className="absolute top-0 left-0 right-0 h-6">
                {/* Traits verticaux des heures dans le header */}
                {Array.from({ length: 25 }, (_, i) => (
                  <div
                    key={`header-line-${i}`}
                    className="absolute top-0 bottom-0 w-px bg-gray-300"
                    style={{ left: `${(i / 24) * 100}%` }}
                  />
                ))}

                {/* Labels des heures centrés entre les traits */}
                {Array.from({ length: 24 }, (_, i) => (
                  <div
                    key={`header-label-${i}`}
                    className="absolute top-0 bottom-0 flex items-center justify-center"
                    style={{
                      left: `${(i / 24) * 100}%`,
                      width: `${(1 / 24) * 100}%`
                    }}
                  >
                    <span className="text-xs font-medium text-gray-600">
                      {i.toString().padStart(2, '0')}h
                    </span>
                  </div>
                ))}
              </div>

              <div className="absolute top-6 left-0 right-0 bottom-0">
                {/* Traits verticaux des intervalles 15min */}
                {Array.from({ length: 96 }, (_, i) => (
                  <div
                    key={`interval-${i}`}
                    className="absolute top-0 bottom-0 w-px bg-gray-100"
                    style={{ left: `${(i / 96) * 100}%` }}
                  />
                ))}

                {/* Traits verticaux des heures - alignés avec le header */}
                {Array.from({ length: 25 }, (_, i) => (
                  <div
                    key={`hour-${i}`}
                    className="absolute top-0 bottom-0 w-px bg-gray-300"
                    style={{ left: `${(i / 24) * 100}%` }}
                  />
                ))}

                {existingTasks.map((task, index) => {
                  const left = timeToPercent(task.startTime);
                  const width = durationToPercent(task.duration);

                  return (
                    <div
                      key={index}
                      className={`absolute ${getStatusColor()} rounded opacity-90 border border-gray-400 hover:opacity-100 transition-opacity cursor-pointer`}
                      style={{
                        left: `${left}%`,
                        width: `${width}%`,
                        top: '8px',
                        bottom: '8px',
                        minWidth: '20px'
                      }}
                      onMouseEnter={(e) => handleMouseEnter(task.id, e)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="absolute inset-0 flex items-center justify-center px-2">
                        <span className="text-[10px] text-white font-semibold truncate">
                          {task.title}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {newTask && newTaskTime && (
                  <div
                    draggable
                    onDragStart={handleDragStart}
                    onDrag={handleDrag}
                    onDragEnd={handleDragEnd}
                    className={`absolute bg-primary-500 rounded border-2 ${
                      hasConflict ? 'border-red-500 shadow-red-500/50' : 'border-blue-600'
                    } shadow-lg cursor-grab active:cursor-grabbing hover:shadow-xl transition-all ${
                      hasConflict && isDragging ? 'animate-pulse' : ''
                    }`}
                    style={{
                      left: `${timeToPercent(newTaskTime)}%`,
                      width: `${durationToPercent(newTask.duration)}%`,
                      top: '8px',
                      bottom: '8px',
                      minWidth: '20px'
                    }}
                    onMouseEnter={(e) => handleMouseEnter('new-task', e)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div className="absolute inset-0 flex items-center justify-center px-2">
                      <span className="text-xs text-white font-semibold truncate">
                        {newTask.title}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {hoveredTask && (() => {
            const tooltipContent = getTooltipContent(hoveredTask.id);
            if (!tooltipContent) return null;

            return (
              <div
                className="fixed z-[75] pointer-events-none animate-fade-in"
                style={{
                  left: `${hoveredTask.x}px`,
                  top: `${hoveredTask.y - 8}px`,
                  transform: 'translate(-50%, -100%)'
                }}
              >
                <div className="bg-gray-900 text-white px-3 py-2 rounded-lg shadow-xl text-sm">
                  <div className="font-semibold mb-1">{tooltipContent.title}</div>
                  <div className="flex items-center gap-2 text-xs text-gray-300">
                    <span>{tooltipContent.time}</span>
                    <span>•</span>
                    <span>{formatDuration(tooltipContent.duration)}</span>
                    {tooltipContent.isNew && (
                      <>
                        <span>•</span>
                        <span className="text-blue-300 font-medium">Nouvelle</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900 mx-auto" />
              </div>
            );
          })()}
        </div>

        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500">
          <span>Utilisez les flèches ou glissez pour naviguer sur les 24 heures</span>
        </div>
      </div>
    </div>
  );
}
